import { ReactElement, useEffect, useState } from 'react';
import { Box, ButtonBase, Typography, alpha, useTheme } from '@mui/material';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import EventBusyOutlined from '@mui/icons-material/EventBusyOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getBatchExpiringCount } from '@/data/batchService';
import { getBranchStocks } from '@/data/stockService';

interface StockAlertsCardProps {
  /**
   * Branch in scope. Without one the card is hidden: the low-stock count
   * needs every stock row in scope (thresholds are applied per row), which
   * in admin view is the whole company or platform.
   */
  branchId: number | null;
  showLowStock: boolean;
  showExpiry: boolean;
}

interface Counts {
  lowStock: number | null;
  expiring: number | null;
}

/**
 * "Kritik stokta 5 ürün · SKT'si 3 gün içinde dolan 2 parti" above the
 * dashboard tabs; a click opens the stock page. Hidden when it cannot load.
 */
const StockAlertsCard = ({
  branchId,
  showLowStock,
  showExpiry
}: StockAlertsCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [counts, setCounts] = useState<Counts | null>(null);

  useEffect(() => {
    // A previous branch's counts must not stay on screen.
    setCounts(null);
    if (!branchId || (!showLowStock && !showExpiry)) return;
    let cancelled = false;
    Promise.all([
      showLowStock
        ? getBranchStocks({
            branchId,
            lowStockOnly: true,
            page: 0,
            limit: 1
          })
            .then((result) => result.total)
            .catch(() => null)
        : Promise.resolve(null),
      showExpiry
        ? getBatchExpiringCount(branchId)
            .then((result) => result.total)
            .catch(() => null)
        : Promise.resolve(null)
    ]).then(([lowStock, expiring]) => {
      if (!cancelled) setCounts({ lowStock, expiring });
    });
    return () => {
      cancelled = true;
    };
  }, [branchId, showLowStock, showExpiry]);

  if (!counts || (counts.lowStock === null && counts.expiring === null)) {
    return null;
  }

  const items = [
    counts.lowStock !== null && {
      key: 'low',
      count: counts.lowStock,
      icon: <WarningAmberRounded />,
      text: t('stockAlerts.lowStock', { n: counts.lowStock }),
      color: theme.palette.warning.main
    },
    counts.expiring !== null && {
      key: 'expiry',
      count: counts.expiring,
      icon: <EventBusyOutlined />,
      text: t('stockAlerts.expiring', { n: counts.expiring }),
      color: theme.palette.error.main
    }
  ].filter(Boolean) as {
    key: string;
    count: number;
    icon: ReactElement;
    text: string;
    color: string;
  }[];
  const allClear = items.every((item) => item.count === 0);

  return (
    <Box
      data-testid="stock-alerts"
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}
    >
      {allClear ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            color: theme.palette.success.main,
            background: alpha(theme.palette.success.main, 0.08)
          }}
        >
          <CheckCircleOutline fontSize="small" />
          <Typography variant="body2">{t('stockAlerts.allClear')}</Typography>
        </Box>
      ) : (
        items
          .filter((item) => item.count > 0)
          .map((item) => (
            <ButtonBase
              key={item.key}
              onClick={() => navigate('/management/stock')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                color: item.color,
                border: `1px solid ${alpha(item.color, 0.4)}`,
                background: alpha(item.color, 0.08),
                '&:hover': { background: alpha(item.color, 0.16) }
              }}
            >
              {item.icon}
              <Typography variant="body2" fontWeight={600}>
                {item.text}
              </Typography>
            </ButtonBase>
          ))
      )}
    </Box>
  );
};

export default StockAlertsCard;
